import React, { FC, Fragment, ChangeEvent, useState, useEffect } from 'react';
import { CircularProgress, Grid, TextField, Typography, makeStyles } from '@material-ui/core';
import { Autocomplete, AutocompleteProps } from '@material-ui/lab';

import { Group, useGroupSuggestLazyQuery, GroupSuggestion, GroupFragment } from 'api';
import { GroupAvatar } from './index';

type CustomProps<T> = Omit<AutocompleteProps<T>, 'renderInput' | 'options'>;

interface IProps extends CustomProps<GroupSuggestion | Group> {
  value?: Partial<GroupSuggestion | Group | GroupFragment>;
  onChange: (group: Partial<GroupSuggestion | GroupFragment>) => void;
  validationError: string | string[];
}

const useStyles = makeStyles(theme => ({
  avatar: {
    marginLeft: theme.spacing(2),
  },
  input: {
    '&::-webkit-input-placeholder': {
      opacity: 1,
    },
    '&::-moz-placeholder': {
      opacity: 1,
    },
    '&:-ms-input-placeholder': {
      opacity: 1,
    },
    '&::-ms-input-placeholder': {
      opacity: 1,
    },
  },
}));

const SuggestGroup: FC<IProps> = ({ placeholder, value, onChange, validationError, ...rest }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [loadGroups, { loading, data }] = useGroupSuggestLazyQuery({
    variables: { prefix: inputValue },
  });

  const options = getOptions();

  function getOptions() {
    if (!data || !data.suggest) {
      return [];
    }

    return data.suggest.results;
  }

  function getVariant(): any {
    return !!value ? 'standard' : 'outlined';
  }

  function handleChange(event: ChangeEvent, nextValue: Group) {
    onChange(nextValue);
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    setInputValue(event.target.value);
  }

  useEffect(() => {
    if (!loading) {
      loadGroups();
    }
  }, [inputValue]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      options={options}
      loading={loading}
      onChange={handleChange}
      getOptionSelected={option => {
        return option.title === value.title;
      }}
      getOptionLabel={option => option.title}
      renderOption={option => (
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <GroupAvatar group={option as GroupSuggestion} />
          </Grid>
          <Grid item>
            <Typography>{option.title}</Typography>
          </Grid>
        </Grid>
      )}
      renderInput={params => (
        <TextField
          {...params}
          variant={getVariant() as any}
          placeholder={!!value && value.title !== '' ? value.title : placeholder}
          onChange={handleInputChange}
          error={!!validationError}
          helperText={validationError}
          InputProps={{
            ...params.InputProps,
            classes: { input: classes.input },
            startAdornment: !!value && (
              <GroupAvatar className={classes.avatar} group={value as GroupSuggestion} />
            ),
            endAdornment: (
              <Fragment>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </Fragment>
            ),
          }}
        />
      )}
      {...rest}
    />
  );
};

export default SuggestGroup;
